import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { SectionCard } from './SectionCard';
import { containedButtonStyle } from '@/theme';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LogSection, SectionUnitsEnum, LogTemplateType } from '@/__generated__/graphql';

type Mode = 'ReadOnly' | 'Edit';

type LogSectionsListProps = {
  mode: Mode;
  logSections: LogSection[];
  onChangeLogSections: (sections: LogSection[]) => void;
  hostLogTemplateType: LogTemplateType;
};

export const LogSectionsList = ({
  mode,
  logSections,
  onChangeLogSections,
  hostLogTemplateType,
}: LogSectionsListProps) => {
  const handleAddSection = () => {
    const nextOrder = logSections.length + 1;

    const newSection: LogSection = {
      id: generateMongoObjectId(),
      key: '',
      title: 'New Section',
      sectionUnit: SectionUnitsEnum.Hours,
      order: nextOrder,
      defaultOpen: true,
      fieldKeys: [],
    };

    onChangeLogSections([...logSections, newSection]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = logSections.findIndex((s) => s.key === active.id);
    const newIndex = logSections.findIndex((s) => s.key === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(logSections, oldIndex, newIndex).map((section, index) => ({
      ...section,
      order: index + 1,
    }));

    onChangeLogSections(reordered);
  };

  return (
    <Stack spacing={2}>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={logSections.map((s) => s.key)}
          strategy={verticalListSortingStrategy}
        >
          {logSections.map((section) => (
            <SortableSectionWrapper key={section.key} id={section.key} mode={mode}>
              <SectionCard
                section={section}
                mode={mode}
                onChange={(updated) =>
                  onChangeLogSections(logSections.map((s) => (s.id === updated.id ? updated : s)))
                }
                onRemove={(sectionToRemove) =>
                  onChangeLogSections(logSections.filter((s) => s.id !== sectionToRemove.id))
                }
                hostLogTemplateType={hostLogTemplateType}
              />
            </SortableSectionWrapper>
          ))}
        </SortableContext>
      </DndContext>

      {mode === 'Edit' && (
        <Box>
          <Divider sx={{ my: 2 }} />
          <Button
            startIcon={<AddCircleIcon />}
            variant="contained"
            sx={containedButtonStyle}
            onClick={handleAddSection}
          >
            Add New Form
          </Button>
        </Box>
      )}
    </Stack>
  );
};

const SortableSectionWrapper = ({
  id,
  children,
  mode,
}: {
  id: string;
  children: React.ReactNode;
  mode: Mode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragHandleProps = mode === 'Edit' ? { ...attributes, ...listeners } : {};

  return (
    <div ref={setNodeRef} style={style}>
      {React.cloneElement(children as React.ReactElement, { dragHandleProps })}
    </div>
  );
};
